import { 
  IonContent, IonHeader, IonPage, IonTitle, 
  IonToolbar, IonButton, IonGrid, IonRow, 
  IonCol, IonList, IonIcon, IonItemOption,
  IonItemSliding, IonCheckbox, IonItemOptions,
  IonButtons, IonItem, IonLabel, IonAlert,
  IonModal, IonCard, IonCardContent, useIonViewWillEnter,
  IonFooter, IonInput, IonItemDivider, IonNote,
  IonChip
} from '@ionic/react';

import { 
  informationCircleOutline, trashOutline,
  downloadOutline, closeOutline, cloudOfflineOutline,
  pencilOutline
} from 'ionicons/icons';

import { useState, useEffect, useRef, useCallback } from 'react';
import { storageManager } from '../utils/storageManager'
import OnlineIndicator from '../components/OnlineIndicator';
import { 
  useSavedCalculations ,
  SavedCalculation
} from '../hooks/calculatorHooks'
import { useAppPreferences } from '../hooks/preferencesHooks';
import { CSV_CONFIG } from '../utils/constants';
import { decimalToFraction, formatMeasurement, fractionToDecimal } from '../utils/formatUtils';
import './styles/SavedCalculations.css';

const SavedCalculations: React.FC = () => {
  // Use the saved calculations hook
  const {
    savedCalculations,
    setSavedCalculations,
    storageWarning,
    setStorageWarning,
    loadSavedCalculations
  } = useSavedCalculations();

  // Get user preferences for sorting
  const { preferences, isLoading: preferencesLoading, loadPreferences } = useAppPreferences();

  // State variables
  const [ selectedItems, setSelectedItems ] = useState<number[]>([]);
  const [ isOnline, setIsOnline ] = useState<boolean>(navigator.onLine);
  const [ selectedCalculation, setSelectedCalculation ] = useState<SavedCalculation | null>(null);
  const [ showDetails, setShowDetails ] = useState<boolean>(false);
  const [ showDeleteAlert, setShowDeleteAlert ] = useState<boolean>(false);
  const [ idToDelete, setIdToDelete ] = useState<number | null>(null);
  const [ showExportPrompt, setShowExportPrompt ] = useState<boolean>(false);
  const [ exportFilename, setExportFilename ] = useState<string>('');
  const [ calculationsToExport, setCalculationsToExport ] = useState<SavedCalculation[]>([]);
  const [ sortedCalculations, setSortedCalculations ] = useState<SavedCalculation[]>([]);
  const [ showRenamePrompt, setShowRenamePrompt ] = useState<boolean>(false);

  // Reference for IonItemSliding elements
  const slidingItemsRef = useRef<{ [key: number]: any }>({});

  // Helper function to check online status and show warning if offline
  const checkOnlineStatus = (): boolean => {
    if (!isOnline) {
      setStorageWarning({
        show: true,
        message: "You're currently offline. Please connect to the internet to export calculations."
      });
      return false;
    }
    return true;
  };

  // Helper function to safely get calculation result values (supporting both old and new property names)
  const getResultValue = (results: any, primaryKey: string, fallbackKey?: string): number => {
    return Number(results[primaryKey] || (fallbackKey ? results[fallbackKey] : 0) || 0);
  };

  // Helper function to extract numeric values from calculation inputs/results
  const extractCalculationValues = (calc: SavedCalculation) => {
    return {
      H: Number(calc.inputs.H || 0),
      D: Number(calc.inputs.D || 0),
      SUPER: Number(calc.inputs.SUPER || 0),
      SE: getResultValue(calc.results, 'se', 'SE'),
      EE: getResultValue(calc.results, 'ee', 'EE'),
      CE: getResultValue(calc.results, 'ce', 'CE'),
      LLLEClearance: getResultValue(calc.results, 'llleclearance', 'LLLEClearance'),
      clearance: Number(calc.results.clearance || 0),
      division: calc.inputs.division || ''
    };
  };

  // Helper function to format chip values
  const formatChipValue = (value: number): string => {
    // Return decimal format with up to 3 decimal places, removing trailing zeros
    const formatted = value.toFixed(3).replace(/\.?0+$/, '');
    return `${formatted}"`;
  };

  // Function to check and display sorting message

  //Apply sorting based on preferences
  const applySortPreference = useCallback(() => {
    if (savedCalculations.length === 0) {
      // Make sure sortedCalculations is also empty when savedCalculations is empty
      setSortedCalculations([]);
      return;
    }
    
    // Default to "Newest First" if preferences aren't loaded yet
    const sortingPreference = preferencesLoading ? "Newest First" : preferences.defaultSorting;
    
    const sorted = [...savedCalculations].sort((a, b) => {
      if (sortingPreference === "Newest First") {
        // Assuming calculations have a timestamp property
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      } else if (sortingPreference === "Oldest First") {
        return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      } else if (sortingPreference === "Alphabetical") {
        // Sort alphabetically by calculation name (A-Z)
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      } else {
        // Fallback to newest first
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      }
    });
    
    setSortedCalculations(sorted);
  }, [savedCalculations, preferences.defaultSorting, preferencesLoading]);

  // Run sorting when calculations or preferences change
  useEffect(() => {
    if (!preferencesLoading) {
      applySortPreference();
    }
  }, [savedCalculations, preferences.defaultSorting, preferencesLoading, applySortPreference]);

  // Re-apply sorting when tab becomes active
  useIonViewWillEnter(() => {
    // First load the latest preferences and calculations
    loadPreferences();
    
    // Explicitly reload calculations from localStorage
    // This ensures we always have the latest data when navigating to this tab
    loadSavedCalculations().then(() => {
      // Apply sorting after both preferences and calculations are loaded
      applySortPreference();
    });
  });

  // Track online status
  useEffect(() => {
    const handleOnlineStatusChange = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);

    return () => {
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
    };
  }, []);

  // Reset selection when calculations change
  useEffect(() => {
    setSelectedItems([]);
  }, [savedCalculations]);

  // Toggle selection of an item
  const toggleSelect = (id: number) => {
    setSelectedItems(prev => {
      if (prev.includes(id)) {
        return prev.filter(itemId => itemId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Handle showing calculation details
  const showCalculationDetails = (calc: SavedCalculation) => {
    setSelectedCalculation(calc);
    setShowDetails(true);
  };

  // Delete a calculation
  const onDelete = (id: number) => {
    setIdToDelete(id);
    setShowDeleteAlert(true);

    // Close the sliding item if it's open
    if (slidingItemsRef.current[id]) {
      slidingItemsRef.current[id].close();
    }
  };

  // Confirm and execute deletion
  const handleDelete = async () => {
    if (idToDelete === null) return;

    const updatedCalculations = savedCalculations.filter(calc => calc.id !== idToDelete);

    // Use storageManager to save the updated list
    const saveResult = await storageManager.saveCalculations(updatedCalculations);

    if (saveResult.success) {
      setSavedCalculations(updatedCalculations);

      // Close details modal if the deleted item is currently viewed
      if (selectedCalculation && selectedCalculation.id === idToDelete) {
        setShowDetails(false);
      }
    } else {
      // Show error notification if save failed
      setStorageWarning({
        show: true,
        message: saveResult.message || 'Failed to delete calculation'
      });
    }

    // Reset state
    setIdToDelete(null);
    setShowDeleteAlert(false);
  };

  // Helper function to determine chart letter based on H value, division, and GOR distance
  const getChartLetter = (heightValue: number, division: string, gorDistance?: number): string => {
    // Only proceed if division is either "A Division" or "B Division"
    if (division !== 'A Division' && division !== 'B Division') {
      return ''; // Return empty string if division is not found
    }

    // For above-train calculations (GOR <= 0), use GOR distance to determine chart letter
    if (gorDistance !== undefined && gorDistance <= 0) {
      return getChartLetterFromGOR(gorDistance, division);
    }

    // For wayside measurements (GOR > 0), use height-based chart letter assignment
    return getChartLetterFromHeight(heightValue, division);
  };

  // Helper function to determine chart letter based on GOR distance for above-train calculations
  const getChartLetterFromGOR = (gorDistance: number, division: string): string => {
    if (division === 'A Division') {
      // A Division GOR-based chart letter assignment for above-train calculations
      if (gorDistance === -28.25) return 'K';
      if (gorDistance > -28.25 && gorDistance < -16.25) return 'J-K';
      if (gorDistance === -16.25) return 'J';
      if (gorDistance > -16.25 && gorDistance < -4.25) return 'I-J';
      if (gorDistance === -4.25) return 'I';
      if (gorDistance > -4.25 && gorDistance < 0) return 'H-I';
      if (gorDistance === 0) return 'H-I'; // GOR between [0, -4.25) includes 0
    } else if (division === 'B Division') {
      // B Division GOR-based chart letter assignment for above-train calculations
      if (gorDistance === -28.25) return 'J';
      if (gorDistance > -28.25 && gorDistance < -7.25) return 'I-J';
      if (gorDistance === -7.25) return 'I';
      if (gorDistance > -7.25 && gorDistance < 0) return 'H-I';
      if (gorDistance === 0) return 'H-I'; // GOR between [0, -7.25) includes 0
    }

    return ''; // Fallback for unexpected values
  };

  // Helper function to determine chart letter based on height for wayside measurements
  const getChartLetterFromHeight = (heightValue: number, division: string): string => {
    // Convert the height value to fraction format for comparison
    const heightFraction = decimalToFraction(heightValue);
    
    // Lookup tables with height values in string format
    const heightCharts: Record<string, { heights: string[], labels: string[] }> = {
      'A Division': {
        heights: ['-1/2', '10 3/8', '21 7/16', '42 3/8', '114 1/4', '120 1/8', '132 1/2', '139 1/8', '143 11/16', '145 5/16', '145 5/8'],
        labels:  ['A',    'B',      'C',       'D',      'E',       'F',       'G',       'H',       'I',         'J',        'K'      ]
      },
      'B Division': {
        heights: ['-1/2', '11', '42 3/8', '69 3/4', '115 3/8', '129 5/8', '140 1/8', '144 3/4', '147 3/16', '148 1/2'],
        labels:  ['A',    'B',  'C',      'D',      'E',       'F',       'G',       'H',       'I',         'J',    ]
      }
    };

    // Get appropriate chart based on division
    const chart = heightCharts[division];
    const { heights, labels } = chart;

    // Convert height strings to decimals for comparison
    const heightDecimals = heights.map(fractionToDecimal);
    
    // Convert our input height to decimal (after formatting as fraction)
    const heightDecimalValue = fractionToDecimal(heightFraction);
    
    // Exact match (compare the decimal values)
    const exactIndex = heightDecimals.findIndex(h => Math.abs(h - heightDecimalValue) < 0.001);
    if (exactIndex !== -1) {
      return labels[exactIndex];
    }

    // Find where height falls between values
    for (let i = 0; i < heightDecimals.length - 1; i++) {
      if (heightDecimalValue > heightDecimals[i] && heightDecimalValue < heightDecimals[i + 1]) {
        return `${labels[i]}-${labels[i + 1]}`;
      }
    }

    // If height is below the lowest value
    if (heightDecimalValue < heightDecimals[0]) {
      return `<${labels[0]}`;
    }

    // If height is above the highest value
    if (heightDecimalValue > heightDecimals[heightDecimals.length - 1]) {
      return `>${labels[labels.length - 1]}`;
    }

    // Fallback
    return '';
  };

  // Generate CSV content for export
  const convertToCSV = (calculations: SavedCalculation[]): string => {
    // CSV Headers
    let csvContent = CSV_CONFIG.HEADERS.join(',') + '\n';
    
    // Add data rows 
    calculations.forEach(calc => {
      const values = extractCalculationValues(calc);

      // For CENTER or END EXCESS, use whichever value is not 0
      let centerOrEndExcess;
      if (values.EE !== 0) {
        centerOrEndExcess = formatMeasurement(values.EE).replace('"', '" EE');
      } else if (values.CE !== 0) {
        centerOrEndExcess = formatMeasurement(values.CE).replace('"', '" CE');
      } else {
        centerOrEndExcess = `0"`;
      }

      // Determine chart letter based on height, division, and GOR distance
      const chartLetter = getChartLetter(values.H, values.division, values.D);

      // Determine clearance status
      const clearanceStatus = values.clearance >= 0 ? 'CLEAR' : 'NOT CLEAR';

      // Ballasted track and vertical curvature data (only for GOR -28.25)
      const isGorAt28_25 = values.D === -28.25;
      const ballastedTrackValue = isGorAt28_25 ? 
        (calc.ballastedTrack ? 'Yes' : 'No') : '-';
      const verticalCurvatureValue = isGorAt28_25 ? 
        (calc.verticalCurvature ? 'Yes' : 'No') : '-';

      csvContent += `"${calc.name}",` +                       // EQUIPMENT NAME
                    `,` +                                     // EQUIPMENT TYPE (empty cell)
                    `${formatMeasurement(values.H, true)},` + // HEIGHT OF EQUIP. FROM T.O.R.
                    `${chartLetter},` +                       // CHART LETTER
                    `${formatMeasurement(values.D)},` +       // DISTANCE TO G.O.R.
                    `"${calc.track || ''}",` +                // TRACK (populated with track name if available)
                    `${centerOrEndExcess},` +                 // CENTER OR END EXCESS with EE or CE appended
                    `${formatMeasurement(values.SUPER)},` +   // SUPER ELEVATION
                    `${formatMeasurement(values.SE)},` +             // SUPER ELEV. EXCESS
                    `${formatMeasurement(values.LLLEClearance)},` +  // MIN. DISTANCE REQUIRED
                    `${formatMeasurement(values.clearance)},` +      // TOTAL CLEARANCE
                    `${clearanceStatus},` +                   // CLEARANCE STATUS
                    `"${ballastedTrackValue}",` +             // BALLASTED TRACK
                    `"${verticalCurvatureValue}"\n`;          // VERTICAL CURVATURE
    });
    return csvContent;
  };

  // Export a single calculation
  const onExport = (id: number) => {
    if (!checkOnlineStatus()) return;

    const calcToExport = savedCalculations.find(calc => calc.id === id);
    if (calcToExport) {
      // Close the sliding item if it's open
      if (slidingItemsRef.current[id]) {
        slidingItemsRef.current[id].close();
      }

      // Prepare export with default filename
      const defaultName = `${calcToExport.name.replace(/\s+/g, '_')}`;
      prepareExport([calcToExport], defaultName);
    }
  };

  // Export multiple calculations
  const onExportAll = () => {
    if (!checkOnlineStatus()) return;

    if (savedCalculations.length === 0) {
      setStorageWarning({
        show: true,
        message: "No calculations to export."
      });
      return
    }

    // Determine which calculations to export
    const calcsToExport = selectedItems.length > 0
      ? savedCalculations.filter(calc => selectedItems.includes(calc.id))
      : savedCalculations;

    // Create default file name
    const defaultName = `${new Date().toISOString().split('T')[0]}_calculations`;
    prepareExport(calcsToExport, defaultName);
  };

  // Prepare export and show filename prompt
  const prepareExport = (calculations: SavedCalculation[], defaultName: string) => {
    setCalculationsToExport(calculations);
    setExportFilename(defaultName);
    setShowExportPrompt(true);
  };

  // Handle the actual export process
  const handleExportCalculations = async (calculations: SavedCalculation[], fileName: string) => {
    try {
      if (calculations.length === 0) {
        setStorageWarning({
          show: true,
          message: "No calculations to export."
        });
        return;
      }

      // Generate CSV content
      const csvContent = convertToCSV(calculations);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create file object first - before we try to check if we can share it
      const file = new File([blob], fileName, { 
        type: 'text/csv' 
      });

      // Use Web Share API if available and can share this file
      if (navigator.share && 
          typeof navigator.canShare === 'function' && 
          navigator.canShare({ files: [file] })) {
        try {
          // Use Web Share API
          await navigator.share({
            files: [file],
            title: 'Export Calculations',
          });
        } catch (shareError) {
          // Fallback if Web Share API fails
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } else {
        // Fallback for browsers without Web Share API
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      return true;
    } catch (error) {
      console.error("Export failed:", error);
      setStorageWarning({
        show: true,
        message: "Export failed. Please try again."
      });
      return false;
    }
  };

  // Execute the export with the provided filename
  const executeExport = async () => {
    let finalFilename = exportFilename.trim();

    // Ensure filename has .csv extension
    if (!finalFilename.toLowerCase().endsWith('.csv')) {
      finalFilename = `${finalFilename}.csv`;
    }

    const success = await handleExportCalculations(calculationsToExport, finalFilename);

    if (success) {
      setShowExportPrompt(false);
      setExportFilename('');
    }
  };

  // Handle opening the rename dialog
  const openRenameDialog = () => {
    if (selectedCalculation) {
      setShowRenamePrompt(true);
    }
  };

  // Handle renaming a calculation
  const executeRename = async (inputName: string) => {
    if (!selectedCalculation || !inputName.trim()) {
      return;
    }

    const updatedName = inputName.trim();
    
    // Update the calculation in the local array
    const updatedCalculations = savedCalculations.map(calc => 
      calc.id === selectedCalculation.id 
        ? { ...calc, name: updatedName }
        : calc
    );

    // Save to storage
    const saveResult = await storageManager.saveCalculations(updatedCalculations);

    if (saveResult.success) {
      setSavedCalculations(updatedCalculations);
      
      // Update the selected calculation for the modal
      setSelectedCalculation({ ...selectedCalculation, name: updatedName });
    } else {
      // Show error notification if save failed
      setStorageWarning({
        show: true,
        message: saveResult.message || 'Failed to rename calculation'
      });
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Saved Calculations</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">Saved Calculations</IonTitle>
            <IonButtons slot="end">
              <OnlineIndicator />
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        {/* Storage warning message */}
        {storageWarning && storageWarning.show && (
          <IonItem color="warning" lines="none">
            <IonLabel className="ion-text-wrap">
              {storageWarning.message}
            </IonLabel>
            <IonButton
              slot="end"
              fill="clear"
              onClick={() => setStorageWarning({ ...storageWarning, show: false })}
            >
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonItem>
        )}

        <IonGrid>

          <IonRow>
            <IonCol>

              <IonButton
                expand="block"
                color="secondary"
                strong={true}
                type="button"
                disabled={savedCalculations.length === 0 || !isOnline}
                onClick={onExportAll}
              >
                {selectedItems.length > 0 
                  ? `Export ${selectedItems.length} Calculations` 
                  : 'Export All Calculations'}
              </IonButton>

              {!isOnline && (
                <IonNote color="danger" className="ion-padding-start">
                  <IonIcon icon={cloudOfflineOutline} /> Connect to internet to export
                </IonNote>
              )}

            </IonCol>
          </IonRow>

          <IonRow>
            <IonCol>
              {sortedCalculations.length === 0 ? (
                <IonCard>
                  <IonCardContent className="ion-text-center ion-padding">
                    <p>No saved calculations yet</p>
                  </IonCardContent>
                </IonCard>
              ) : (
                <IonList inset={false}>
                  {sortedCalculations.map(calc => (
                    <IonItemSliding 
                      key={calc.id}
                      ref={(el) => {
                        if (el) slidingItemsRef.current[calc.id] = el;
                      }}
                    >
                      <IonItem button detail={false} onClick={() => showCalculationDetails(calc)}>
                        <IonCheckbox
                          slot="start"
                          checked={selectedItems.includes(calc.id)}
                          onIonChange={() => toggleSelect(calc.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <IonLabel>
                          <h2>{storageManager.truncateText(calc.name, 30)}</h2>
                          <p className="timestamp-line">
                            {new Date(calc.timestamp).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })} {new Date(calc.timestamp).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                          </p>
                          <div className="metrics-chips">
                            {(() => {
                              const values = extractCalculationValues(calc);
                              const clearanceColor = values.clearance >= 0 ? 'success' : 'danger';
                              const clearanceStatus = values.clearance >= 0 ? 'Pass' : 'Fail';
                              
                              return (
                                <>
                                  <IonChip 
                                    color={clearanceColor}
                                    className="compact-chip"
                                    aria-label={`Clearance: ${formatChipValue(values.clearance)}, ${clearanceStatus}`}
                                  >
                                    <IonLabel>{formatChipValue(values.clearance)}</IonLabel>
                                  </IonChip>
                                  <IonChip 
                                    color="medium"
                                    className="compact-chip"
                                    aria-label={`TOR: ${formatChipValue(values.H)}`}
                                  >
                                    <IonLabel>TOR {formatChipValue(values.H)}</IonLabel>
                                  </IonChip>
                                  <IonChip 
                                    color="medium"
                                    className="compact-chip"
                                    aria-label={`GOR: ${formatChipValue(values.D)}`}
                                  >
                                    <IonLabel>GOR {formatChipValue(values.D)}</IonLabel>
                                  </IonChip>
                                </>
                              );
                            })()}
                          </div>
                        </IonLabel>
                        <IonButtons slot="end">
                          <IonButton 
                            fill="clear" 
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(calc.id);
                            }}
                          >
                            <IonIcon icon={trashOutline} />
                          </IonButton>
                        </IonButtons>
                      </IonItem>

                      <IonItemOptions side="end">
                        <IonItemOption 
                          color="primary" 
                          onClick={() => onExport(calc.id)}
                          disabled={!isOnline}
                        >
                          Export
                        </IonItemOption>

                        <IonItemOption color="danger" onClick={() => onDelete(calc.id)}>
                          Delete
                        </IonItemOption>

                      </IonItemOptions>
                    </IonItemSliding>
                  ))}
                </IonList>
              )}
            </IonCol>
          </IonRow>

        </IonGrid>

        {/* Details Modal */}
        <IonModal 
          isOpen={showDetails} 
          onDidDismiss={() => setShowDetails(false)}
          canDismiss={true}
          presentingElement={undefined}
        >
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton 
                  fill="clear" 
                  size="small"
                  onClick={openRenameDialog}
                  aria-label="Rename calculation"
                >
                  <IonIcon icon={pencilOutline} />
                </IonButton>
              </IonButtons>
              <IonTitle>
                {selectedCalculation?.name}
              </IonTitle>
              <IonButtons slot="end">
                <IonButton 
                  fill="clear" 
                  size="large"
                  onClick={() => setShowDetails(false)}
                  aria-label="Close modal"
                >
                  <IonIcon icon={closeOutline} size="large" />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          <IonContent>
            {selectedCalculation && (
              <>
                {/* Input Values Section */}
                <IonCard className="ion-margin">
                  <IonCardContent>
                    <IonList inset={false} lines="inset">
                      <IonItemDivider color="primary">
                        <IonLabel>Input Values</IonLabel>
                      </IonItemDivider>
                      
                      <IonItem>
                        <IonLabel>Division</IonLabel>
                        <IonNote slot="end">{selectedCalculation.inputs?.division}</IonNote>
                      </IonItem>
                      
                      {selectedCalculation.track && (
                        <IonItem>
                          <IonLabel>Track</IonLabel>
                          <IonNote slot="end">{selectedCalculation.track}</IonNote>
                        </IonItem>
                      )}
                      
                      <IonItem>
                        <IonLabel>Track Type</IonLabel>
                        <IonNote slot="end">
                          {selectedCalculation.inputs.trackType === 'tangent' ? 'Tangent' : 'Curved'}
                        </IonNote>
                      </IonItem>
                      
                      <IonItem>
                        <IonLabel>Side of Track</IonLabel>
                        <IonNote slot="end">
                          {selectedCalculation.inputs.direction === 'inside' || selectedCalculation.inputs.direction === 'IN'
                            ? (selectedCalculation.inputs.trackType === 'tangent' ? 'Side of Lower Rail' : 'Inside of Curve') 
                            : (selectedCalculation.inputs.trackType === 'tangent' ? 'Side of Higher Rail' : 'Outside of Curve')}
                        </IonNote>
                      </IonItem>
                      
                      <IonItem>
                        <IonLabel>Height from T.O.R.</IonLabel>
                        <IonNote slot="end">{decimalToFraction(Number(selectedCalculation.inputs.H || 0))}</IonNote>
                      </IonItem>
                      
                      <IonItem>
                        <IonLabel>Distance to G.O.R.</IonLabel>
                        <IonNote slot="end">{decimalToFraction(Number(selectedCalculation.inputs.D || 0))}</IonNote>
                      </IonItem>
                      
                      <IonItem>
                        <IonLabel>Middle Ordinate</IonLabel>
                        <IonNote slot="end">{decimalToFraction(Number(selectedCalculation.inputs.MO || 0))}</IonNote>
                      </IonItem>
                      
                      <IonItem lines={Number(selectedCalculation.inputs.D) === -28.25 ? "inset" : "none"}>
                        <IonLabel>Super Elevation</IonLabel>
                        <IonNote slot="end">{decimalToFraction(Number(selectedCalculation.inputs.SUPER || 0))}</IonNote>
                      </IonItem>
                      
                      {/* Show ballasted track and vertical curvature fields only for GOR -28.25 calculations */}
                      {Number(selectedCalculation.inputs.D) === -28.25 && (
                        <>
                          <IonItem>
                            <IonLabel>Ballasted Track</IonLabel>
                            <IonNote slot="end">
                              {selectedCalculation.ballastedTrack ? 'Yes' : 'No'}
                            </IonNote>
                          </IonItem>
                          <IonItem lines="none">
                            <IonLabel>Vertical Curvature</IonLabel>
                            <IonNote slot="end">
                              {selectedCalculation.verticalCurvature ? 'Yes' : 'No'}
                            </IonNote>
                          </IonItem>
                        </>
                      )}
                    </IonList>
                  </IonCardContent>
                </IonCard>

                {/* Results Section */}
                <IonCard className="ion-margin">
                  <IonCardContent>
                    <IonList inset={false} lines="inset">
                      <IonItemDivider color="primary">
                        <IonLabel>Results</IonLabel>
                      </IonItemDivider>
                      
                      <IonItem>
                        <IonLabel>Curve Radius</IonLabel>
                        <IonNote slot="end">
                          {getResultValue(selectedCalculation.results, 'r', 'R') === 0 ? "N/A" :
                           `${decimalToFraction(getResultValue(selectedCalculation.results, 'r', 'R')).replace('"', "'")}`}
                        </IonNote>
                      </IonItem>
                      
                      <IonItem>
                        <IonLabel>Super Elevation Excess</IonLabel>
                        <IonNote slot="end">{decimalToFraction(getResultValue(selectedCalculation.results, 'se', 'SE'))}</IonNote>
                      </IonItem>
                      
                      <IonItem>
                        <IonLabel>End Excess</IonLabel>
                        <IonNote slot="end">{decimalToFraction(getResultValue(selectedCalculation.results, 'ee', 'EE'))}</IonNote>
                      </IonItem>
                      
                      <IonItem>
                        <IonLabel>Center Excess</IonLabel>
                        <IonNote slot="end">{decimalToFraction(getResultValue(selectedCalculation.results, 'ce', 'CE'))}</IonNote>
                      </IonItem>
                      
                      <IonItem>
                        <IonLabel>Min. Distance Required</IonLabel>
                        <IonNote slot="end">{decimalToFraction(getResultValue(selectedCalculation.results, 'minreq', 'LLLEMinReq'))}</IonNote>
                      </IonItem>
                      
                      <IonItem lines="none">
                        <IonLabel>LLLE with Excess</IonLabel>
                        <IonNote slot="end">{decimalToFraction(getResultValue(selectedCalculation.results, 'llleclearance', 'LLLEClearance'))}</IonNote>
                      </IonItem>
                    </IonList>
                  </IonCardContent>
                </IonCard>

                {/* Final Result Card */}
                <IonCard className="ion-margin" color={Number(selectedCalculation.results.clearance || 0) >= 0 ? "success" : "danger"}>
                  <IonCardContent className="ion-text-center ion-padding">
                    <h2 className="ion-margin-bottom">Calculated Clearance</h2>
                    <h1 className="ion-no-margin">
                      {decimalToFraction(Number(selectedCalculation.results.clearance || 0))}
                    </h1>
                  </IonCardContent>
                </IonCard>
              </>
            )}
          </IonContent>

          <IonFooter>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton color="danger" onClick={() => onDelete(selectedCalculation!.id)}>
                  Delete
                </IonButton>
              </IonButtons>
              <IonButtons slot="end">
                <IonButton
                  color="primary"
                  onClick={() => onExport(selectedCalculation!.id)}
                  disabled={!isOnline}
                >
                  Export
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonFooter>
        </IonModal>

        {/* Delete Confirmation Alert */}
        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}       
          header="Confirm Deletion"
          message="Are you sure you want to delete this calculation? This action cannot be undone."
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel'
            },
            {
              text: 'Delete',
              role: 'destructive',
              handler: handleDelete
            }
          ]}
        />

        {/* Export filename prompt */}
        <IonModal 
          isOpen={showExportPrompt} 
          onDidDismiss={() => setShowExportPrompt(false)}
        >
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowExportPrompt(false)}>Cancel</IonButton>
              </IonButtons>
              <IonButtons slot="end">
                <IonButton strong={true} onClick={executeExport}>Export</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          <IonContent className="ion-padding">
            <IonItem lines="none">
              <IonLabel position="stacked">
                <h1>Name your export file</h1>
                <p>.csv will be added automatically</p>
              </IonLabel>
            </IonItem>
            <IonItem lines="none" className="ion-padding-bottom">
              <IonInput
                value={exportFilename}
                style={{ width: '80%' }}
                slot="start"
                placeholder="clearance_calculations"
                onIonChange={(e) => setExportFilename(e.detail.value || '')}
              />
              <IonNote slot="end">.csv</IonNote>
            </IonItem>
          </IonContent>
        </IonModal>

        {/* Rename calculation alert */}
        <IonAlert
          isOpen={showRenamePrompt}
          onDidDismiss={() => setShowRenamePrompt(false)}
          header="Rename Calculation"
          inputs={[
            {
              name: 'calculationName',
              type: 'text',
              placeholder: 'Enter calculation name',
              value: selectedCalculation?.name || ''
            }
          ]}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel'
            },
            {
              text: 'Save',
              handler: (data) => {
                if (data.calculationName && data.calculationName.trim()) {
                  executeRename(data.calculationName);
                  return true; // Allow the alert to close
                }
                return false; // Prevent the alert from closing if no name is provided
              }
            }
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default SavedCalculations;
